import { useState } from 'react';
import { useInRouterContext } from 'react-router-dom';
import { SCRIPT_SUB_SLUGS } from '@scoutaround/script-breakdown-shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, awaitingPaidReading, type Requirement } from '../../lib/api.js';
import { RequirementTag, SourcingTag } from '../../components/StatusTag.js';
import { Callout } from '../../components/Callout.js';
import { Icon } from '../../components/Icon.js';
import { ScriptIntake } from '../../components/ScriptIntake.js';
import { ScriptProgressPanel } from '../../components/ScriptProgressPanel.js';
import { useScriptRun } from '../../lib/useScriptRun.js';
import { SceneReviewWorkspace } from '../../components/scenes/SceneReviewWorkspace.js';
import {
  LocalSceneAddress,
  RoutedSceneAddress,
  type SceneAddress,
  type SetSceneAddress,
} from '../../components/scenes/sceneAddress.js';
import { Disclosure } from '../../components/ui/Disclosure.js';
import { MoreMenu } from '../../components/ui/MoreMenu.js';
import { StatusPill } from '../../components/ui/StatusPill.js';

import { ScriptReview } from './ScriptReview.js';
import { ReviewRequirements } from './ReviewRequirements.js';
import { UnplacedSceneConversations } from '../../components/conversations/ConversationDrawer.js';
import { NeedsReadingPanel } from '../../components/credits/NeedsReadingPanel.js';

type SubTab = keyof typeof SCRIPT_SUB_SLUGS;

const SUB_BY_SLUG = Object.fromEntries(
  Object.entries(SCRIPT_SUB_SLUGS).map(([k, v]) => [v, k as SubTab]),
) as Record<string, SubTab>;

export function ScriptAndScenesTab({
  projectId,
  subSlug,
  onNavigate,
}: {
  projectId: string;
  subSlug: string | undefined;
  onNavigate: (path: string) => void;
}) {
  // Unknown sub-slug falls back rather than erroring: the tab itself is a valid
  // destination, and the sub-view is a refinement of it.
  const sub: SubTab = (subSlug ? SUB_BY_SLUG[subSlug] : undefined) ?? 'SCRIPT';
  const setSub = (next: SubTab) => {
    onNavigate(`/projects/${projectId}/script/${SCRIPT_SUB_SLUGS[next]}`);
  };

  return (
    <div data-testid="script-scenes-tab">
      <div className="sw-subtabs" data-framed={sub === 'SCRIPT' ? '0' : '1'}>
        {(['SCRIPT', 'REVIEW', 'BREAKDOWN', 'REQUIREMENTS'] as const).map((s) => (
          <button
            key={s}
            type="button"
            className="sub"
            data-on={sub === s ? '1' : '0'}
            data-testid={`subtab-${s}`}
            onClick={() => {
              setSub(s);
            }}
          >
            {s === 'SCRIPT'
              ? 'Script'
              : s === 'REVIEW'
                ? 'Script Review'
                : s === 'BREAKDOWN'
                  ? 'Review requirements'
                  : 'Sourcing'}
          </button>
        ))}
      </div>

      {/* THE SCENE REVIEW BRINGS ITS OWN SURFACES (a summary, the list, the
          scene), so it is not boxed in a second frame; the other views keep
          theirs. */}
      {sub === 'SCRIPT' ? (
        <div className="sw-tab-body">
          <ScriptPanel
            projectId={projectId}
            onRequirementAdded={() => {
              setSub('REQUIREMENTS');
            }}
            onOpenBreakdown={() => {
              setSub('BREAKDOWN');
            }}
            onContinue={() => {
              onNavigate(`/projects/${projectId}/choose`);
            }}
          />
        </div>
      ) : (
        <div style={{ border: '1px solid var(--color-divider)', padding: 24, background: 'var(--color-bg)' }}>
          {sub === 'REVIEW' ? <ScriptReview projectId={projectId} /> : null}
          {sub === 'BREAKDOWN' ? (
            <BreakdownReviewPanel
              projectId={projectId}
              onContinueToMatching={() => {
                onNavigate(`/projects/${projectId}/explore`);
              }}
            />
          ) : null}
          {sub === 'REQUIREMENTS' ? <RequirementsPanel projectId={projectId} /> : null}
        </div>
      )}
    </div>
  );
}

function ScriptPanel({
  projectId,
  onRequirementAdded,
  onOpenBreakdown,
  onContinue,
}: {
  projectId: string;
  onRequirementAdded: () => void;
  /** The AI breakdown lives on Review requirements; this page only points at it. */
  onOpenBreakdown: () => void;
  /** Every scene confirmed: on to the choice of workflows. */
  onContinue: () => void;
}) {
  const queryClient = useQueryClient();
  const [uploadedVersionId, setUploadedVersionId] = useState<string | null>(null);
  /** The form is hidden once a screenplay is loaded; this is the way back to
   *  it, and it closes itself as soon as a new one lands. */
  const [replacing, setReplacing] = useState(false);
  const routed = useInRouterContext();

  /**
   * The project's existing screenplay.
   *
   * WITHOUT THIS THE TAB WAS BLANK on every page load. The version id lived
   * only in the component state set by an upload, so a producer who uploaded
   * yesterday came back to an empty upload form — scenes, elements and
   * requirements all present in the database and none of them reachable.
   */
  const current = useQuery({
    queryKey: ['current-script', projectId],
    queryFn: () => api.currentScript(projectId),
  });

  // An upload in THIS session wins, because the producer is looking at what
  // they just added and the stored answer may still be a moment behind.
  const versionId = uploadedVersionId ?? current.data?.scriptVersionId ?? null;

  // The same rule the setup flow learned the hard way: when a run ends, read
  // what it wrote again — the answer on screen is otherwise the one fetched
  // before it existed.
  const { inFlight, progress } = useScriptRun(versionId, {
    onFinished: () => {
      void Promise.all([
        queryClient.invalidateQueries({ queryKey: ['requirements', projectId] }),
        queryClient.invalidateQueries({ queryKey: ['review-progress', projectId] }),
        queryClient.invalidateQueries({ queryKey: ['review-view', versionId] }),
        queryClient.invalidateQueries({ queryKey: ['scene-validation', projectId] }),
        queryClient.invalidateQueries({ queryKey: ['current-script', projectId] }),
      ]);
    },
  });
  const extracting = inFlight && (progress?.stages.some((s) => s.stage === 'EXTRACT') ?? false);

  // Replacing keeps "Add manually" able to scope a requirement to the current
  // screenplay's scenes, as it could when the list sat under the form.
  const scenesForScope = useQuery({
    queryKey: ['scenes', versionId],
    queryFn: () => api.listScenes(versionId as string),
    enabled: replacing && versionId !== null,
  });
  const failed = progress?.status === 'FAILED';

  const workspace = (address: SceneAddress, set: SetSceneAddress) => (
    <SceneReviewWorkspace
      projectId={projectId}
      mode="tab"
      scriptVersionId={versionId}
      address={address}
      onAddress={set}
      onContinue={onContinue}
      intro={<UnplacedSceneConversations projectId={projectId} />}
    />
  );

  return (
    <div className="sw-script" data-testid="script-panel">
      {/* WHAT IS ALREADY HERE, in one line: which draft, how much of it was
          read, and where the AI breakdown lives. Replacing it is one menu away
          — the form no longer stands above the scenes for ever. */}
      {current.data ? (
        <div className="sw-strip" data-testid="script-current-file">
          <span className="sw-strip-icon">
            <Icon name="script" size={20} />
          </span>
          <span className="sw-strip-text">
            <span className="sw-strip-title">{current.data.title}</span>
            <span className="sw-strip-meta">
              {`Version ${String(current.data.versionNumber)} · ${String(current.data.sceneCount)} scenes read`}
              {current.data.createdAt === null
                ? ''
                : ` · added ${new Date(current.data.createdAt).toLocaleDateString('en-IE', { day: '2-digit', month: '2-digit', year: 'numeric' })}`}
            </span>
          </span>
          {inFlight ? (
            <StatusPill state="PROCESSING" label={extracting ? 'AI breakdown running' : 'Reading the screenplay'} testId="script-run-status" />
          ) : failed ? (
            <StatusPill state="FAILED" label="Stopped" testId="script-run-status" />
          ) : null}
          <span className="sw-strip-actions">
            <button
              type="button"
              className="ctl"
              data-testid="script-open-breakdown"
              title="The AI location breakdown is run and reviewed under Review requirements"
              onClick={onOpenBreakdown}
            >
              AI breakdown <Icon name="arrowRight" size={13} />
            </button>
            <MoreMenu
              testId="script-more"
              label="Screenplay actions"
              items={[
                {
                  id: 'replace',
                  label: replacing ? 'Keep the current screenplay' : 'Replace the screenplay',
                  testId: 'script-replace',
                  onSelect: () => {
                    setReplacing((v) => !v);
                  },
                },
              ]}
            />
          </span>
        </div>
      ) : null}

      {/* A SCANNED PDF WAITING FOR ITS PAID READING (P6): nothing reads it
          until somebody approves the estimate here or on step 1. */}
      {current.data && !replacing && awaitingPaidReading(current.data) !== null ? (
        <div style={{ marginTop: 14 }}>
          <NeedsReadingPanel
            projectId={projectId}
            scriptVersionId={current.data.scriptVersionId}
            pageCount={awaitingPaidReading(current.data)?.pageCount ?? 0}
          />
        </div>
      ) : null}

      {versionId !== null && !replacing && (inFlight || failed) ? (
        <Disclosure summary="Processing details" testId="script-processing" defaultOpen>
          <ScriptProgressPanel scriptVersionId={versionId} />
        </Disclosure>
      ) : null}

      {versionId === null || replacing ? (
        <div className="sw-intake">
          <ScriptIntake
            projectId={projectId}
            scenes={(scenesForScope.data ?? []).map((sc) => ({ id: sc.id, sceneNumber: sc.sceneNumber, slugline: sc.slugline }))}
            onSubmitted={(scriptVersionId) => {
              setUploadedVersionId(scriptVersionId);
              setReplacing(false);
            }}
            onRequirementAdded={onRequirementAdded}
          />
          {replacing ? (
            <button
              type="button"
              className="ctl"
              data-testid="script-replace-cancel"
              onClick={() => {
                setReplacing(false);
              }}
            >
              Keep the current screenplay
            </button>
          ) : null}
        </div>
      ) : routed ? (
        <RoutedSceneAddress>{workspace}</RoutedSceneAddress>
      ) : (
        <LocalSceneAddress>{workspace}</LocalSceneAddress>
      )}
    </div>
  );
}

/**
 * Script Review — where a producer answers "what locations does this screenplay
 * actually need?"
 *
 * APPROVAL AND SOURCING ARE SEPARATE CONTROLS, because they are separate
 * decisions (ADR 0004). Approving says the interpretation is right; activating
 * says start hunting now. A producer who approves twelve requirements in a
 * planning session has not thereby authorised twelve searches.
 */
function RequirementsPanel({ projectId }: { projectId: string }) {
  const queryClient = useQueryClient();

  const progress = useQuery({
    queryKey: ['review-progress', projectId],
    queryFn: () => api.reviewProgress(projectId),
  });

  const matchable = useQuery({
    queryKey: ['matchable', projectId],
    queryFn: () => api.matchableRequirements(projectId),
  });

  const invalidate = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['review-progress', projectId] }),
      queryClient.invalidateQueries({ queryKey: ['matchable', projectId] }),
    ]);
  };

  const match = useMutation({
    mutationFn: (requirementId: string) => api.runMatching(projectId, requirementId),
    onSuccess: invalidate,
  });

  const p = progress.data;

  return (
    <div data-testid="requirements-panel">
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 16, flexWrap: 'wrap' }}>
        <p className="dh" style={{ fontSize: 22 }}>
          Master location list
        </p>
        {p ? (
          <p className="mm" style={{ fontSize: 12.5, margin: 0 }} data-testid="review-header">
            {p.total} requirement{p.total === 1 ? '' : 's'} · {p.approved} approved · {p.proposed}{' '}
            awaiting review · {p.sourcing} being sourced
          </p>
        ) : null}
      </div>

      {p?.reviewComplete ? (
        <div style={{ marginTop: 14 }}>
          <Callout tone="ok" title="Review complete" testId="review-complete">
            Every proposal has been resolved. That is a statement about the review — it does not
            mean every requirement is being sourced.
          </Callout>
        </div>
      ) : null}

      {matchable.data && matchable.data.length === 0 ? (
        <p className="mm" style={{ fontSize: 13, marginTop: 18 }} data-testid="no-sourcing">
          Nothing is being sourced yet. A requirement has to be approved and then activated before
          matching runs.
        </p>
      ) : null}

      <div style={{ marginTop: 18, display: 'grid', gap: 10 }}>
        {matchable.data?.map((r: Requirement) => (
          <div key={r.id} className="card1" style={{ padding: '16px 18px' }} data-testid={`requirement-${r.id}`}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <span className="dh" style={{ fontSize: 19 }}>
                {r.title}
              </span>
              <RequirementTag status={r.status} />
              <SourcingTag status={r.sourcingStatus} />
              <button
                type="button"
                className="ctl"
                style={{ marginLeft: 'auto' }}
                data-testid={`match-${r.id}`}
                disabled={match.isPending}
                onClick={() => {
                  match.mutate(r.id);
                }}
              >
                {match.isPending ? 'Matching…' : 'Find locations'}
              </button>
            </div>
          </div>
        ))}
      </div>

      {match.data ? (
        <p className="mm" style={{ fontSize: 12.5, marginTop: 14 }} data-testid="match-result">
          Assessed {match.data.assessed} location{match.data.assessed === 1 ? '' : 's'};{' '}
          {match.data.suggested} met every must-have.
        </p>
      ) : null}

      {match.isError ? (
        <div style={{ marginTop: 14 }}>
          <Callout tone="warn" title="Matching did not run" testId="match-error">
            {match.error.message}
          </Callout>
        </div>
      ) : null}
    </div>
  );
}

/**
 * The design-19 review screen needs a script version; this finds the current
 * one and says so plainly when there is none.
 *
 * A project with no screenplay is a NORMAL state, not an error — it is where
 * every project starts — so it gets a sentence and a direction rather than an
 * empty frame or a spinner that never resolves.
 */
/**
 * The location breakdown inside the Script tab: the same review as `/breakdown`,
 * with the same way on once every requirement has a decision — Explore, where
 * the approved ones are searched for.
 */
function BreakdownReviewPanel({
  projectId,
  onContinueToMatching,
}: {
  projectId: string;
  onContinueToMatching: () => void;
}) {
  const current = useQuery({
    queryKey: ['current-script', projectId],
    queryFn: () => api.currentScript(projectId),
  });

  if (current.isPending) {
    return (
      <p className="mm" style={{ fontSize: 13 }} data-testid="breakdown-review-loading">
        Loading the screenplay…
      </p>
    );
  }

  const script = current.data;

  if (!script) {
    return (
      <p className="mm" style={{ fontSize: 13 }} data-testid="breakdown-review-empty">
        No screenplay yet. Upload or paste one on the Script tab, then analyse it — the breakdown
        appears here for you to confirm.
      </p>
    );
  }

  return (
    <ReviewRequirements
      projectId={projectId}
      scriptVersionId={script.scriptVersionId}
      projectTitle={script.title}
      scriptVersionLabel={`V${String(script.versionNumber)}`}
      onContinueToMatching={onContinueToMatching}
    />
  );
}
